// C:\xampp\htdocs\my-project\frontend\src\api\api.js
import axios from "axios";
import { toast } from "react-toastify";

let attemptedRefresh = false;

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:8000/api",
  timeout: 200000,
});

/* ---------------- helpers ---------------- */
const isPublicPath = (pathname) =>
  /^\/($|login(\/|$)|register(\/|$)|set-password(\/|$)|forgot-password(\/|$))/.test(
    pathname || ""
  );

const hasPwdChangeSession = () =>
  typeof window !== "undefined" && !!sessionStorage.getItem("pwd_change_token");

const setTokenExpiry = (accessToken) => {
  try {
    const [, payload] = String(accessToken).split(".");
    if (!payload) return;
    const data = JSON.parse(atob(payload));
    const expMs = Number(data?.exp) * 1000;
    if (Number.isFinite(expMs)) localStorage.setItem("token_expiry", String(expMs));
  } catch {}
};

const safeToast = (msg) => {
  try {
    toast.error(msg);
  } catch {}
};

/* ---------------- request ---------------- */
api.interceptors.request.use((config) => {
  const hasPwdToken = !!config.headers?.["X-Password-Change-Token"];
  const noAuth = config.headers?.["X-No-Auth"] === "1";

  // Do not attach Bearer when using first-login token
  if (!hasPwdToken && !noAuth) {
    const token = localStorage.getItem("access_token");
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  }

  // IMPORTANT: when sending FormData, let the browser set the boundary.
  // If a caller set Content-Type manually, remove it to avoid broken uploads.
  if (config.data instanceof FormData && config.headers?.["Content-Type"]) {
    delete config.headers["Content-Type"];
  }

  return config;
});

/* ---------------- response ---------------- */
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const resp = error?.response;
    const cfg = error?.config || {};
    const status = resp?.status;
    const path = window.location?.pathname || "/";
    const skipRedirect = cfg?.headers?.["X-Skip-Auth-Redirect"] === "1";
    const isRefreshCall = /\/refresh(\/|$)/.test(String(cfg?.url || ""));

    // Never hijack first-login flow
    if (status === 423 || hasPwdChangeSession()) {
      if (status === 423) error.isPasswordChangeRequired = true;
      return Promise.reject(error);
    }

    // Try refresh once on 401
    if (status === 401 && !attemptedRefresh && !isRefreshCall) {
      attemptedRefresh = true;
      const refreshToken = localStorage.getItem("refresh_token");

      if (refreshToken) {
        try {
          const refreshResp = await api.post(
            "/refresh",
            { refresh_token: refreshToken },
            { validateStatus: () => true, headers: { "X-Skip-Auth-Redirect": "1" } }
          );

          if (refreshResp.status >= 200 && refreshResp.status < 300) {
            const newAccessToken = refreshResp.data?.access_token;
            if (newAccessToken) {
              localStorage.setItem("access_token", newAccessToken);
              setTokenExpiry(newAccessToken);
              cfg.headers = cfg.headers || {};
              cfg.headers.Authorization = `Bearer ${newAccessToken}`;
              attemptedRefresh = false;
              return api(cfg);
            }
          }

          // refresh failed
          attemptedRefresh = false;
          if (!skipRedirect && !isPublicPath(path)) {
            safeToast("Your session has expired. Please log in again.");
            localStorage.clear();
            window.location.replace("/login");
            return new Promise(() => {});
          }
          return Promise.reject(error);
        } catch {
          attemptedRefresh = false;
          if (!skipRedirect && !isPublicPath(path)) {
            safeToast("Your session has expired. Please log in again.");
            localStorage.clear();
            window.location.replace("/login");
            return new Promise(() => {});
          }
          return Promise.reject(error);
        }
      } else {
        if (!skipRedirect && !isPublicPath(path)) {
          safeToast("Your session has expired. Please log in again.");
          localStorage.clear();
          window.location.replace("/login");
          return new Promise(() => {});
        }
        return Promise.reject(error);
      }
    }

    // Other errors
    if (status === 500) safeToast("Internal server error. Please try again later.");
    else if (status === 404) safeToast("The requested resource was not found.");
    else if (!skipRedirect) safeToast("An unexpected error occurred.");

    return Promise.reject(error);
  }
);

/* ---- optional: token expiry check on startup ---- */
const checkTokenExpiry = async () => {
  try {
    const expiry = Number(localStorage.getItem("token_expiry"));
    if (!expiry) return;
    if (Date.now() >= expiry) {
      const refreshToken = localStorage.getItem("refresh_token");
      if (!refreshToken) return;
      await api.post(
        "/refresh",
        { refresh_token: refreshToken },
        { validateStatus: () => true, headers: { "X-Skip-Auth-Redirect": "1" } }
      );
    }
  } catch {}
};
checkTokenExpiry();

export default api;
